import { Button, Form, Offcanvas } from 'react-bootstrap';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import type { ObjectType } from '@econtractor/types';
import { useAuth } from 'context/AuthContext';
import { useSettings } from 'context/SettingsContext';
import { useTheme } from 'context/ThemeContext';
import { ObjectSelected } from 'modules/objects';
import { canViewSales } from 'utils/helpers/access/hasUserAccess';
import { selectStylesDark, selectStylesLigth } from 'utils/helpers/structs/selectStyles';
import RequestObjectChip from '../RequestObjectChip';
import RequestStatusBadge from '../RequestStatusBadge';
import { toFilterObject } from '../model/supplierRequestFiltersStorage';
import type { SupplierRequestFilterState, SupplierRequestObjectMode } from '../model/types';
import {
    buildStatusSelectStyles,
    toStatusOptions,
    toVendorOptions,
    type StatusOption,
} from './helper';

const objectModeBtnStyle = {
    height: '24px',
    minHeight: '24px',
    padding: '0 8px',
    lineHeight: '1',
};

interface OffFilterProps {
    show: boolean;
    draftFilter: SupplierRequestFilterState;
    setDraftFilter: React.Dispatch<React.SetStateAction<SupplierRequestFilterState>>;
    onApply: () => void;
    onReset: () => void;
    onHide: () => void;
}

const formatStatusOption = (option: StatusOption) => (
    <RequestStatusBadge status={option.value} />
);

const OffFilter = ({
    show,
    draftFilter,
    setDraftFilter,
    onApply,
    onReset,
    onHide,
}: OffFilterProps) => {
    const { t } = useTranslation(['Vendors', 'common']);
    const { auth } = useAuth();
    const { settings } = useSettings();
    const { darkTheme } = useTheme();
    const allowSales = canViewSales(auth?.user);
    const [selectTheme, setSelectTheme] = useState(darkTheme ? selectStylesDark : selectStylesLigth);
    const [objectMode, setObjectMode] = useState<SupplierRequestObjectMode>('object');

    const vendorOptions = useMemo(
        () => toVendorOptions(settings?.DIRS?.Vendor),
        [settings?.DIRS?.Vendor],
    );

    const statusOptions = useMemo(() => toStatusOptions(t), [t]);

    const statusSelectStyles = useMemo(
        () => buildStatusSelectStyles(selectTheme),
        [selectTheme],
    );

    useEffect(() => {
        setSelectTheme(darkTheme ? selectStylesDark : selectStylesLigth);
    }, [darkTheme]);

    useEffect(() => {
        if (!allowSales && objectMode === 'sale') setObjectMode('object');
    }, [allowSales, objectMode]);

    const handleAddObject = (object: ObjectType | null) => {
        const next = toFilterObject(object);
        if (!next) return;
        setDraftFilter((prev) => {
            if (prev.objects?.some((item) => item.objectId === next.objectId)) return prev;
            return { ...prev, objects: [...(prev.objects || []), next] };
        });
    };

    const handleRemoveObject = (objectId: number) => {
        setDraftFilter((prev) => ({
            ...prev,
            objects: (prev.objects || []).filter((item) => item.objectId !== objectId),
        }));
    };

    const objectTitle =
        objectMode === 'sale'
            ? t('supplierRequests.create.modeSale')
            : t('supplierRequests.create.modeObject');

    return (
        <Offcanvas show={show} onHide={onHide} placement="end">
            <Offcanvas.Header closeButton className="border-bottom">
                <Offcanvas.Title>{t('supplierRequests.filter.title')}</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                <div className="off-filter-form d-flex flex-column h-100">
                    <div className="off-filter-content px-3">
                        <Form.Group className="mb-3">
                            <label className="form-label text-muted">
                                {t('supplierRequests.filter.labelVendors')}
                            </label>
                            <Select
                                options={vendorOptions}
                                styles={selectTheme}
                                value={vendorOptions.filter((v) =>
                                    draftFilter.vendors.includes(v.value),
                                )}
                                onChange={(value) => {
                                    const selected = Array.isArray(value) ? value : [];
                                    setDraftFilter((prev) => ({
                                        ...prev,
                                        vendors: selected.map((v) => v.value),
                                    }));
                                }}
                                isMulti
                                placeholder={t('supplierRequests.filter.placeholderVendors')}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <ObjectSelected
                                key={objectMode}
                                value={null}
                                onChange={handleAddObject}
                                title={t('supplierRequests.filter.labelObjects')}
                                onlySale={objectMode === 'sale'}
                                clearable={false}
                                maxHeight={180}
                                className="mb-0"
                                rightElement={
                                    allowSales ? (
                                        <div
                                            className="btn-group btn-group-sm flex-shrink-0 ms-2 mb-1"
                                            role="group"
                                            aria-label={objectTitle}
                                        >
                                            <button
                                                type="button"
                                                className={`btn ${
                                                    objectMode === 'object'
                                                        ? 'btn-primary'
                                                        : 'btn-outline-primary'
                                                }`}
                                                style={objectModeBtnStyle}
                                                onClick={() => setObjectMode('object')}
                                            >
                                                {t('supplierRequests.create.modeObject')}
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn ${
                                                    objectMode === 'sale'
                                                        ? 'btn-primary'
                                                        : 'btn-outline-primary'
                                                }`}
                                                style={objectModeBtnStyle}
                                                onClick={() => setObjectMode('sale')}
                                            >
                                                {t('supplierRequests.create.modeSale')}
                                            </button>
                                        </div>
                                    ) : null
                                }
                            />
                            {draftFilter.objects?.length ? (
                                <div className="d-flex flex-wrap gap-1 mt-2">
                                    {draftFilter.objects.map((object) => (
                                        <RequestObjectChip
                                            key={object.objectId}
                                            object={object}
                                            onRemove={() => handleRemoveObject(object.objectId)}
                                        />
                                    ))}
                                </div>
                            ) : null}
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <label className="form-label text-muted">
                                {t('supplierRequests.filter.labelStatuses')}
                            </label>
                            <Select<StatusOption, true>
                                options={statusOptions}
                                styles={statusSelectStyles}
                                value={statusOptions.filter((s) =>
                                    draftFilter.statuses.includes(s.value),
                                )}
                                onChange={(value) => {
                                    const selected = Array.isArray(value) ? value : [];
                                    setDraftFilter((prev) => ({
                                        ...prev,
                                        statuses: selected.map((s) => Number(s.value)),
                                    }));
                                }}
                                formatOptionLabel={formatStatusOption}
                                isMulti
                                isSearchable={false}
                                closeMenuOnSelect={false}
                                hideSelectedOptions={false}
                                placeholder={t('supplierRequests.filter.placeholderStatuses')}
                            />
                        </Form.Group>
                    </div>

                    <div className="p-3 offcanvas-footer off-filter-footer">
                        <div className="d-flex justify-content-end gap-2">
                            <Button variant="secondary" onClick={onReset}>
                                {t('common:buttons.reset')}
                            </Button>
                            <Button variant="info" className="min100px" onClick={onApply}>
                                {t('common:buttons.apply')}
                            </Button>
                        </div>
                    </div>
                </div>
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default OffFilter;
